import React, { useState } from "react";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

const REASONS = ["harassment", "spam", "fake_profile", "scam", "inappropriate_content", "other"];

export default function ReportDialog({ open, onClose, userId, contextMatchId, contextMessageId }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("harassment");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        reported_user_id: userId,
        reason,
        description: description.trim() || undefined,
      };
      if (contextMatchId) payload.related_match_id = contextMatchId;
      if (contextMessageId) payload.related_message_id = contextMessageId;
      await api.post("/reports", payload);
      toast.success(t("app.moderation.reportDone"));
      setDescription("");
      setReason("harassment");
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 || detail === "duplicate_recent_report") {
        toast.error(t("app.moderation.reportDuplicate"));
      } else {
        toast.error(extractError(e, t("app.moderation.reportError")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(10,17,40,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="report-dialog-overlay"
    >
      <div
        className="max-w-md w-full rounded-2xl p-6"
        style={{ backgroundColor: "hsl(var(--nvl-bg))", border: "1px solid hsl(var(--nvl-border))" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="report-dialog"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="float-right opacity-60 hover:opacity-100"
          data-testid="report-close"
        >
          <X size={16} />
        </button>
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
          style={{ backgroundColor: "hsl(var(--nvl-accent) / 0.12)", color: "hsl(var(--nvl-accent))" }}
        >
          <Flag size={18} />
        </div>
        <h3 className="font-display text-2xl leading-tight" data-testid="report-title">
          {t("app.moderation.reportTitle")}
        </h3>
        <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.moderation.reportBody")}
        </p>

        <div className="mt-5">
          <label className="text-xs block mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.moderation.reportReasonLabel")}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REASONS.map((r) => {
              const active = r === reason;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  data-testid={`report-reason-${r}`}
                  className={`text-left text-xs px-3 py-2 rounded-md border transition-colors ${
                    active
                      ? "bg-[hsl(var(--nvl-ink))] text-[hsl(var(--nvl-bg))] border-transparent"
                      : "hover:bg-[hsl(var(--muted))]"
                  }`}
                  style={{ borderColor: active ? "transparent" : "hsl(var(--nvl-border))" }}
                >
                  {t(`app.moderation.reasons.${r}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs block mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.moderation.reportDescLabel")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
            placeholder={t("app.moderation.reportDescPlaceholder")}
            rows={3}
            className="w-full nvl-input resize-none"
            data-testid="report-description"
          />
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="nvl-btn nvl-btn-outline"
            data-testid="report-cancel"
          >
            {t("app.moderation.cancel")}
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="nvl-btn nvl-btn-primary"
            data-testid="report-submit"
          >
            {t("app.moderation.reportSubmit")}
          </button>
        </div>
      </div>
    </div>
  );
}
